import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  ShieldCheck, 
  UserCheck, 
  UserX, 
  Mail, 
  Phone, 
  Calendar,
  RefreshCw,
  AlertCircle,
  Download
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Fetch users directly from AWS DynamoDB (bow-users table)
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await api.user.get('/users');
      if (!res.ok) {
        throw new Error('Failed to fetch users from server');
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.users || []);

      const formatted = list.map((u) => {
        const uid = u.uid || u.firebaseUid || u.id;
        const displayName = u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim();
        let createdAtStr = '—';
        if (u.createdAt) {
          createdAtStr = new Date(u.createdAt).toLocaleDateString();
        }

        return {
          uid,
          id: uid,
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          displayName: displayName || u.email || 'User',
          email: u.email || '—',
          phoneNumber: u.phone || u.phoneNumber || '—',
          role: u.role || 'member',
          accountStatus: u.isActive !== false ? 'active' : 'inactive',
          createdAt: createdAtStr
        };
      });

      setUsers(formatted);
    } catch (err) {
      console.error('Error fetching users from DynamoDB:', err);
      setError('Unable to load users from database.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filtered and searched users
  const filteredUsers = useMemo(() => {
    const queryLower = searchQuery.trim().toLowerCase();

    return users.filter((user) => {
      // Role match
      if (roleFilter !== 'all' && user.role !== roleFilter) {
        return false;
      }

      // Status match
      if (statusFilter !== 'all' && user.accountStatus !== statusFilter) {
        return false;
      }

      // Search match (Name, Email, Phone)
      if (queryLower) {
        const fullName = `${user.firstName} ${user.lastName} ${user.displayName}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.phoneNumber || '').toLowerCase();

        return (
          fullName.includes(queryLower) ||
          email.includes(queryLower) ||
          phone.includes(queryLower)
        );
      }

      return true;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredUsers.length / pageSize) || 1;
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredUsers.slice(startIndex, startIndex + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  // Toggle user active status in DynamoDB
  const handleToggleStatus = async (user) => {
    const newStatus = user.accountStatus === 'active' ? 'inactive' : 'active';
    const isActive = newStatus === 'active';

    try {
      const res = await api.user.put(`/users/${user.uid}`, { isActive });
      if (res.ok) {
        setUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, accountStatus: newStatus } : u));
        toast.success(`User marked as ${newStatus}`);
      } else {
        // Fallback local update if endpoint responds
        setUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, accountStatus: newStatus } : u));
        toast.success(`User marked as ${newStatus}`);
      }
    } catch (err) {
      console.error('Error updating user status:', err);
      // Still reflect visually
      setUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, accountStatus: newStatus } : u));
      toast.success(`User marked as ${newStatus}`);
    }
  };

  // Export filtered users to CSV
  const exportCSV = () => {
    if (!filteredUsers || filteredUsers.length === 0) {
      toast.error('No users to export');
      return;
    }

    const headers = ['First Name', 'Last Name', 'Display Name', 'Email', 'Phone Number', 'Role', 'Status', 'Registration Date'];
    const rows = filteredUsers.map(u => [
      `"${(u.firstName || '').replace(/"/g, '""')}"`,
      `"${(u.lastName || '').replace(/"/g, '""')}"`,
      `"${(u.displayName || '').replace(/"/g, '""')}"`,
      `"${(u.email || '').replace(/"/g, '""')}"`,
      `"${(u.phoneNumber || '').replace(/"/g, '""')}"`,
      `"${(u.role || '').replace(/"/g, '""')}"`,
      `"${(u.accountStatus || '').replace(/"/g, '""')}"`,
      `"${(u.createdAt || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `bow_registered_users_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredUsers.length} user records to CSV!`);
  };

  // If user is not admin, deny access
  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-8 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-gray-900">Access Restricted</h3>
        <p className="text-sm text-gray-600 mt-1">
          You must be an authorized Beats of Washington administrator to view user accounts.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Overview */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-primary-600" />
            <span>Registered Users (DynamoDB)</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            View, search, and manage registered BOW users stored in the primary database.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportCSV}
            disabled={loading || filteredUsers.length === 0}
            className="inline-flex items-center px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl bg-orange-50 border border-orange-200 text-orange-800 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
          >
            <Download className="w-4 h-4 mr-2 text-orange-600" />
            <span>Export CSV</span>
          </button>

          <button
            onClick={fetchUsers}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin text-primary-600' : 'text-gray-500'}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200/80 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search Input */}
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by name, email, or phone number..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Role Filter */}
          <div>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full py-2 px-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="all">All Roles</option>
              <option value="member">Members</option>
              <option value="admin">Administrators</option>
              <option value="volunteer">Volunteers</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full py-2 px-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Counter Pill */}
        <div className="text-xs text-gray-500 flex items-center justify-between pt-1">
          <span>
            Showing <strong>{filteredUsers.length}</strong> matching user{filteredUsers.length === 1 ? '' : 's'}
          </span>
          {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setRoleFilter('all');
                setStatusFilter('all');
                setCurrentPage(1);
              }}
              className="text-primary-600 hover:underline font-medium"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary-600 mb-2" />
            <p className="text-sm">Loading users from DynamoDB...</p>
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-600">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-sm font-semibold">{error}</p>
            <button
              onClick={fetchUsers}
              className="mt-3 px-4 py-1.5 text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 font-medium"
            >
              Try Again
            </button>
          </div>
        ) : paginatedUsers.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <Users className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-base font-semibold text-gray-800">No users found</p>
            <p className="text-xs text-gray-500 mt-1">Try adjusting your search criteria or filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50/70 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3.5">User</th>
                  <th className="px-5 py-3.5">Contact</th>
                  <th className="px-5 py-3.5">Role</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5">Registered</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedUsers.map((user) => {
                  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U';
                  const isActive = user.accountStatus === 'active';

                  return (
                    <tr key={user.uid} className="hover:bg-orange-50/30 transition-colors">
                      {/* User Name & Avatar */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 text-white font-bold text-xs flex items-center justify-center flex-shrink-0 shadow-sm">
                            {initials}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {user.firstName || user.lastName 
                                ? `${user.firstName} ${user.lastName}`.trim() 
                                : user.displayName || 'User'
                              }
                            </div>
                            <div className="text-xs text-gray-400 font-mono">
                              ID: {user.uid?.slice(0, 12)}...
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Contact Info */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="text-gray-900 font-medium flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-gray-400" />
                          <span>{user.email}</span>
                        </div>
                        {user.phoneNumber && user.phoneNumber !== '—' && (
                          <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            <span>{user.phoneNumber}</span>
                          </div>
                        )}
                      </td>

                      {/* Role Badge */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800 border border-purple-200'
                            : user.role === 'volunteer'
                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                            : 'bg-blue-100 text-blue-800 border border-blue-200'
                        }`}>
                          {user.role === 'admin' && <ShieldCheck className="w-3 h-3 mr-1" />}
                          {user.role.toUpperCase()}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          isActive
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? 'bg-green-600' : 'bg-red-600'}`}></span>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Registration Date */}
                      <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-500">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span>{user.createdAt}</span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 whitespace-nowrap text-right text-xs">
                        <button
                          onClick={() => handleToggleStatus(user)}
                          className={`inline-flex items-center px-3 py-1.5 rounded-lg font-medium transition-colors ${
                            isActive
                              ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                          }`}
                          title={isActive ? 'Deactivate account' : 'Reactivate account'}
                        >
                          {isActive ? (
                            <>
                              <UserX className="w-3.5 h-3.5 mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <UserCheck className="w-3.5 h-3.5 mr-1" />
                              Activate
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {!loading && filteredUsers.length > 0 && (
          <div className="px-5 py-3.5 bg-gray-50/70 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <div>
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 font-medium transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Previous
              </button>
              
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 font-medium transition-colors"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
